import type { MouseEvent } from 'react';
import type { User } from '@vd/types';
import { Avatar } from '../primitives/Avatar';
import { Tooltip } from '../primitives/Tooltip';
import { cn } from '../cn';
import styles from './AssigneeStack.module.css';

export interface AssigneeStackProps {
  assignees: User[];
  creator?: User;
  max?: number;
  size?: 'sm' | 'md';
  onClickAssignee?: (user: User, e: MouseEvent<HTMLElement>) => void;
  /** Opens the assign picker. Event is provided so parents can anchor a popover. */
  onClickAdd?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

export function AssigneeStack({
  assignees, creator, max = 3, size = 'sm',
  onClickAssignee, onClickAdd, className,
}: AssigneeStackProps) {
  const shown = assignees.slice(0, max);
  const overflow = assignees.length - shown.length;
  const avatarSize = size === 'sm' ? 'xs' : 'sm';

  const handleAdd = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onClickAdd?.(e);
  };

  return (
    <span className={cn(styles.row, className)}>
      {creator && (
        <span className={styles.creator}>
          <Tooltip content={`Created by ${creator.name}`}>
            <Avatar name={creator.name} src={creator.avatarUrl} size={avatarSize} />
          </Tooltip>
        </span>
      )}
      <span className={styles.stack}>
        {shown.length === 0 ? (
          <Tooltip content="Assign someone">
            <button
              className={styles.empty}
              onClick={handleAdd}
              aria-label="Assign"
              type="button"
            >
              +
            </button>
          </Tooltip>
        ) : (
          shown.map((u) => (
            <Tooltip key={u.id} content={u.name}>
              <button
                className={styles.avatarBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onClickAssignee?.(u, e);
                }}
                type="button"
              >
                <Avatar name={u.name} src={u.avatarUrl} size={avatarSize} />
              </button>
            </Tooltip>
          ))
        )}
        {overflow > 0 && (
          <span className={styles.more} title={`${overflow} more`}>+{overflow}</span>
        )}
        {shown.length > 0 && onClickAdd && (
          <Tooltip content="Assign someone else">
            <button
              className={styles.addBtn}
              onClick={handleAdd}
              aria-label="Assign more"
              type="button"
            >
              +
            </button>
          </Tooltip>
        )}
      </span>
    </span>
  );
}
